import React, { useEffect, useRef, useState } from 'react';
import {
  MessageCircle, X, Send, Plus, ArrowLeft, Clock, AlertCircle,
  MessageSquare, Ticket, Bot, ChevronRight, ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { UserRole } from '@/constants/roles';
import useChatbotStore from '@/stores/Common/ChatbotStore';
import './AIChatWidget.css';

const getRoutePrefix = (role?: string): string => {
  switch (role) {
    case UserRole.TRAINEE: return '/trainee';
    case UserRole.ADMIN: return '/admin';
    case UserRole.TRAINER: return '/trainer';
    case UserRole.SUPER_ADMIN: return '/superadmin';
    default: return '/trainee';
  }
};

const statusColors: Record<string, string> = {
  open: '#f59e0b',
  assigned: '#3b82f6',
  in_progress: '#8b5cf6',
  pending_user: '#f97316',
  resolved: '#10b981',
  closed: '#6b7280',
};

const AIChatWidget: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');

  const {
    isOpen, activeView, conversations, activeConversationUuid, messages,
    isLoading, isSending, tickets, loadingTickets,
    toggleWidget, setActiveView, fetchConversations, startNewConversation,
    loadConversation, sendMessage, fetchTickets, clearActiveConversation, closeWidget,
  } = useChatbotStore();

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && activeView === 'chat' && activeConversationUuid) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, activeView, activeConversationUuid]);

  // Fetch conversations when widget opens
  useEffect(() => {
    if (isOpen && conversations.length === 0) {
      fetchConversations();
    }
  }, [isOpen]);

  const handleSend = async () => {
    const msg = inputValue.trim();
    if (!msg || isSending) return;

    setInputValue('');

    if (!activeConversationUuid) {
      // Create conversation first, then send once store is updated
      await startNewConversation();
      // After await, the store has the new uuid
      const newUuid = useChatbotStore.getState().activeConversationUuid;
      if (newUuid) {
        await sendMessage(msg);
      }
      return;
    }

    await sendMessage(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const routePrefix = getRoutePrefix(user?.role);

  const renderConversationsList = () => (
    <div className="acw-conversations-list">
      <button className="acw-new-chat-btn" onClick={startNewConversation} disabled={isLoading}>
        <Plus size={16} /> New Conversation
      </button>
      {isLoading ? (
        <div className="acw-loading">Loading...</div>
      ) : conversations.length === 0 ? (
        <div className="acw-empty">
          <Bot size={32} strokeWidth={1.5} />
          <p>No conversations yet</p>
          <span>Start a new chat to get help</span>
        </div>
      ) : (
        conversations.map(conv => (
          <div
            key={conv.uuid}
            className="acw-conversation-item"
            onClick={() => loadConversation(conv.uuid)}
          >
            <div className="acw-conv-icon">
              <MessageSquare size={16} />
            </div>
            <div className="acw-conv-info">
              <div className="acw-conv-title">{conv.title}</div>
              <div className="acw-conv-meta">
                <span className={`acw-conv-status ${conv.status}`}>{conv.status}</span>
                <span className="acw-conv-date">
                  {new Date(conv.updated_at || conv.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            <ChevronRight size={14} className="acw-conv-arrow" />
          </div>
        ))
      )}
    </div>
  );

  const renderTicketsList = () => (
    <div className="acw-tickets-list">
      {loadingTickets ? (
        <div className="acw-loading">Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div className="acw-empty">
          <Ticket size={32} strokeWidth={1.5} />
          <p>No tickets yet</p>
          <span>Tickets created via chat will appear here</span>
        </div>
      ) : (
        tickets.map(ticket => (
          <div key={ticket.id} className="acw-ticket-item">
            <div className="acw-ticket-header">
              <span className="acw-ticket-number">{ticket.ticket_number}</span>
              <span
                className="acw-ticket-status"
                style={{ background: statusColors[ticket.status] || '#6b7280' }}
              >
                {ticket.status.replace('_', ' ')}
              </span>
            </div>
            <div className="acw-ticket-subject">{ticket.subject}</div>
            <div className="acw-ticket-footer">
              <span className="acw-ticket-date">
                <Clock size={12} /> {new Date(ticket.created_at).toLocaleDateString()}
              </span>
              <button
                className="acw-ticket-view-btn"
                onClick={() => {
                  closeWidget();
                  navigate(`${routePrefix}/support/query`);
                }}
              >
                View Details <ExternalLink size={12} />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderMessages = () => (
    <div className="acw-messages">
      {messages.length === 0 && !isSending && (
        <div className="acw-welcome">
          <Bot size={36} strokeWidth={1.5} />
          <h4>Hi! I'm Mkraft Assistant</h4>
          <p>Ask me anything about courses, features, or platform usage. I'm here to help!</p>
        </div>
      )}
      {messages.map((msg, idx) => (
        <div key={idx} className={`acw-message acw-message-${msg.role}`}>
          {msg.role === 'assistant' && (
            <div className="acw-avatar"><Bot size={14} /></div>
          )}
          <div className={`acw-bubble acw-bubble-${msg.role}`}>
            {msg.content}
            {msg.metadata?.ticket && (
              <div className="acw-ticket-banner">
                <Ticket size={14} />
                <span>Ticket {msg.metadata.ticket.ticket_number} created</span>
              </div>
            )}
          </div>
        </div>
      ))}
      {isSending && (
        <div className="acw-message acw-message-assistant">
          <div className="acw-avatar"><Bot size={14} /></div>
          <div className="acw-bubble acw-bubble-assistant acw-typing">
            <span className="acw-dot" />
            <span className="acw-dot" />
            <span className="acw-dot" />
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );

  return (
    <>
      {/* Floating bubble */}
      {!isOpen && (
        <button className="acw-bubble-btn" onClick={toggleWidget} title="AI Assistant">
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="acw-panel">
          {/* Header */}
          <div className="acw-header">
            {activeView === 'chat' && activeConversationUuid ? (
              <>
                <button className="acw-back-btn" onClick={clearActiveConversation} title="Back to conversations">
                  <ArrowLeft size={18} />
                </button>
                <div className="acw-header-title">
                  <Bot size={18} />
                  <span>Mkraft Assistant</span>
                </div>
                <button
                  className="acw-new-chat-header-btn"
                  onClick={async () => {
                    await startNewConversation();
                  }}
                  title="New conversation"
                  disabled={isLoading}
                >
                  <Plus size={16} />
                </button>
              </>
            ) : (
              <div className="acw-header-title">
                <Bot size={18} />
                <span>Mkraft Assistant</span>
              </div>
            )}
            <button className="acw-close-btn" onClick={toggleWidget}>
              <X size={18} />
            </button>
          </div>

          {/* Tabs */}
          <div className="acw-tabs">
            <button
              className={`acw-tab ${activeView === 'chat' || activeView === 'conversations' ? 'active' : ''}`}
              onClick={() => {
                if (activeConversationUuid) {
                  setActiveView('chat');
                } else {
                  setActiveView('conversations');
                  fetchConversations();
                }
              }}
            >
              <MessageSquare size={14} /> Chats
            </button>
            <button
              className={`acw-tab ${activeView === 'tickets' ? 'active' : ''}`}
              onClick={() => { setActiveView('tickets'); fetchTickets(); }}
            >
              <Ticket size={14} /> My Tickets
            </button>
          </div>

          {/* Body */}
          <div className="acw-body">
            {activeView === 'tickets' ? (
              renderTicketsList()
            ) : activeView === 'conversations' && !activeConversationUuid ? (
              renderConversationsList()
            ) : (
              renderMessages()
            )}
          </div>

          {/* Input — only show in chat view or conversations view */}
          {activeView !== 'tickets' && (
            <div className="acw-input-area">
              <input
                ref={inputRef}
                type="text"
                className="acw-input"
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSending}
              />
              <button
                className="acw-send-btn"
                onClick={handleSend}
                disabled={!inputValue.trim() || isSending}
              >
                <Send size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
